"use client";

import { memo, type PointerEvent, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { CheckIcon, CheckCheckIcon } from "lucide-react";

import { getImageType } from "@/lib/constant";
import {
	SupportTicketCategory,
	type AiKnowledgeDraft,
	type CatalogSuggestion,
	type Message as Data,
	type SupportTicketDraft,
} from "@/types";
import {
	cn,
	errorIntoString,
	extractFromS3Url,
	removeUUIDv4Prefix,
	formatMessageTime,
	toMessageDate,
} from "@/lib/utils";
import { isSafeHttpOrHttpsUrl } from "@/lib/security/safe-href";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import ServiceCard, { ServiceCardSkeleton } from "@/components/cards/ServiceCard";
import { ServiceCardTitleClamp } from "@/components/cards/ServiceCardTitleClamp";
import { useCreateSupportTicketMutation } from "@/features/support-tickets/supportTicketApiInjection";
import {
	useMarkSupportTicketDraftSubmittedMutation,
	useRemoveSupportTicketDraftMutation,
	useUpdateAiKnowledgeDraftStatusMutation,
} from "@/features/chat/chatApiInjection";
import { useUpdateAdminChatbotKnowledgeMutation } from "@/features/chatbot/chatbotApiInjection";
import { useGetServiceQuery } from "@/features/services/serviceApiInjection";
import { useToast } from "@/components/ui/use-toast";

type Props = {
	data: Data;
	chatId?: string;
	isGrouped?: boolean;
	messageId?: string;
	isLauraDirectorsGroup?: boolean;
	onAnswerLauraEscalation?: (messageId: string, label: string) => void;
	onReplyMessage?: (messageId: string, label: string) => void;
};

const linkRegex = /\s(https?:\/\/[^\s]+)/g;
const boldRegex = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g;

function getServiceSlugFromSuggestionUrl(url: string) {
	try {
		const parsed = new URL(url.startsWith("/") ? url : url, "https://techxeed.com");
		const [section, slug] = parsed.pathname.split("/").filter(Boolean);
		return section === "services" && slug ? decodeURIComponent(slug) : null;
	} catch {
		return null;
	}
}

function getReplyPreviewText(content: string) {
	const text =
		content
			.split(/\n+/)
			.map((line) => line.trim())
			.find(Boolean) ?? "";
	return text.length > 140 ? `${text.slice(0, 137)}...` : text;
}

function senderTickSrc(senderType?: "USER" | "AI", senderRole?: string) {
	if (senderRole === "DIRECTOR") return "/assets/profile/tick_director.png";
	if (senderType === "AI") return "/assets/profile/tick_premium.png";
	if (senderRole === "ADMIN") return "/assets/profile/tick_admin.png";
	return null;
}

function Message({
	data: {
		content,
		isReceived,
		senderName,
		senderRole,
		senderType,
		clientSupportSenderRole,
		createdAt,
		fileUrls,
		isRead,
		catalogSuggestions,
		supportTicketDraft,
		aiKnowledgeDraft,
		replyToMessage,
	},
	chatId,
	isGrouped = false,
	messageId,
	isLauraDirectorsGroup = false,
	onAnswerLauraEscalation,
	onReplyMessage,
}: Props) {
	const usableFiles = fileUrls.filter((x) => x.length > 0);
	const canAnswerLauraEscalation =
		isLauraDirectorsGroup &&
		isReceived &&
		senderType === "AI" &&
		Boolean(messageId) &&
		content.includes("Customer question:");
	const paragraphs = useMemo(
		() =>
			content
				.split(/\n+/)
				.map((paragraph) => paragraph.trim())
				.filter(Boolean),
		[content],
	);
	const messageTextRef = useRef<HTMLDivElement>(null);
	const [hasMultipleTextLines, setHasMultipleTextLines] = useState(paragraphs.length > 1);
	const escalationQuestion = useMemo(() => {
		const match = content.match(/Customer question:\s*([\s\S]*?)(?:\n\n|Please reply|$)/i);
		return match?.[1]?.trim().replace(/\s+/g, " ") ?? "";
	}, [content]);
	const [showFullTime, setShowFullTime] = useState(false);
	const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
	const [swipeOffset, setSwipeOffset] = useState(0);
	const tickSrc = senderTickSrc(senderType, senderRole);
	const shouldShowSenderName = isReceived && senderName && clientSupportSenderRole !== "STAFF";

	const resetSwipe = () => {
		swipeStartRef.current = null;
		setSwipeOffset(0);
	};

	const handlePointerDown = (event: PointerEvent<HTMLLIElement>) => {
		if (!messageId || !onReplyMessage) return;
		swipeStartRef.current = { x: event.clientX, y: event.clientY };
		event.currentTarget.setPointerCapture(event.pointerId);
	};

	const handlePointerMove = (event: PointerEvent<HTMLLIElement>) => {
		if (!swipeStartRef.current || !messageId || !onReplyMessage) return;
		const deltaX = event.clientX - swipeStartRef.current.x;
		const deltaY = event.clientY - swipeStartRef.current.y;
		if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 12) {
			resetSwipe();
			return;
		}
		setSwipeOffset(Math.min(Math.max(deltaX, 0), 72));
	};

	const handlePointerEnd = () => {
		if (messageId && onReplyMessage && swipeOffset > 48) {
			onReplyMessage(messageId, getReplyPreviewText(content));
		}
		resetSwipe();
	};

	useEffect(() => {
		const element = messageTextRef.current;
		if (!element) return;

		const measureLines = () => {
			const paragraphElements = Array.from(element.querySelectorAll("p"));
			const firstLineElement = paragraphElements[0] ?? element;
			const styles = window.getComputedStyle(firstLineElement);
			const lineHeight = Number.parseFloat(styles.lineHeight);
			const fontSize = Number.parseFloat(styles.fontSize);
			const singleLineHeight = Number.isFinite(lineHeight)
				? lineHeight
				: Number.isFinite(fontSize)
					? fontSize * 1.2
					: 16;
			const renderedLineCount = paragraphElements.reduce((count, paragraph) => {
				const paragraphHeight = paragraph.getBoundingClientRect().height;
				return count + Math.max(1, Math.round(paragraphHeight / singleLineHeight));
			}, 0);

			setHasMultipleTextLines(paragraphs.length > 1 || renderedLineCount > 1);
		};

		measureLines();
		const resizeObserver = new ResizeObserver(measureLines);
		resizeObserver.observe(element);
		return () => resizeObserver.disconnect();
	}, [paragraphs]);

	return (
		<li
			id={messageId ? `message-${messageId}` : undefined}
			className={cn("flex flex-col touch-pan-y", isGrouped ? "mt-2" : "mt-1")}
			style={{
				transform: swipeOffset ? `translateX(${swipeOffset}px)` : undefined,
				transition: swipeOffset ? "none" : "transform 150ms ease",
			}}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerEnd}
			onPointerCancel={resetSwipe}
		>
			{shouldShowSenderName ? (
				<div className="mb-0.5 ml-3 inline-flex items-center gap-1 self-start text-[0.65rem] font-semibold text-muted-foreground">
					<span>{senderName}</span>
					{tickSrc ? (
						<img
							src={tickSrc}
							alt=""
							aria-hidden
							className="size-3 shrink-0 self-center object-contain"
						/>
					) : null}
				</div>
			) : null}
			<div
				className={cn(
					"relative w-fit min-w-32 max-w-[80%] text-wrap text-xs md:text-sm overflow-visible font-light break-words transition-all",
					hasMultipleTextLines ? "p-5" : "p-2.5",
					isReceived
						? "bg-tertiary/80"
						: "self-end bg-secondary/80 text-secondary-foreground",
					"rounded-[1.5rem]",
					!isGrouped && isReceived && "rounded-bl-none",
					!isGrouped && !isReceived && "rounded-br-none",
				)}
			>
				{usableFiles.map((x) => (
					<FileLayer key={x} url={x} />
				))}
				{replyToMessage ? (
					<div
						className={cn(
							"mb-2 rounded-xl border-l-2 px-3 py-2 text-[0.7rem]",
							isReceived
								? "border-secondary bg-background/60 text-foreground"
								: "border-background/80 bg-background/15 text-secondary-foreground",
						)}
					>
						<p className="font-semibold">Replying to</p>
						<p className="mt-0.5 line-clamp-2 opacity-80">
							{getReplyPreviewText(replyToMessage.content)}
						</p>
					</div>
				) : null}
				<div
					ref={messageTextRef}
					className="space-y-2 no-underline [&_a]:break-all [&_a]:underline [&_a]:decoration-solid"
				>
					{paragraphs.map((paragraph, index) => (
						<p key={`${paragraph}-${index}`} className="whitespace-pre-wrap">
							<MessageContent text={paragraph} />
						</p>
					))}
				</div>
				{canAnswerLauraEscalation ? (
					<button
						type="button"
						className="mt-3 rounded-full bg-background/80 px-3 py-1 text-[0.7rem] font-semibold text-secondary shadow-sm transition hover:bg-background"
						onClick={() =>
							messageId &&
							onAnswerLauraEscalation?.(
								messageId,
								escalationQuestion || "selected question",
							)
						}
					>
						Answer this question
					</button>
				) : null}
			</div>
			<div
				className={cn(
					"flex items-center gap-1 mt-1 px-1",
					!isReceived && "self-end flex-row-reverse",
				)}
			>
				<span
					className={cn("text-[0.65rem] text-grey-foreground cursor-help")}
					title={toMessageDate(createdAt)}
					onMouseEnter={() => setShowFullTime(true)}
					onMouseLeave={() => setShowFullTime(false)}
				>
					{showFullTime ? toMessageDate(createdAt) : formatMessageTime(createdAt)}
				</span>
				{!isReceived && <MessageStatus isRead={isRead} />}
			</div>
			{catalogSuggestions && catalogSuggestions.length > 0 ? (
				<CatalogSuggestionCards suggestions={catalogSuggestions} isReceived={isReceived} />
			) : null}
			{supportTicketDraft ? (
				<SupportTicketDraftCard
					draft={supportTicketDraft}
					chatId={chatId}
					messageId={messageId}
					isReceived={isReceived}
				/>
			) : null}
			{aiKnowledgeDraft ? (
				<AiKnowledgeDraftCard
					draft={aiKnowledgeDraft}
					isReceived={isReceived}
					chatId={chatId}
					messageId={messageId}
				/>
			) : null}
		</li>
	);
}

function AiKnowledgeDraftCard({
	draft,
	isReceived,
	chatId,
	messageId,
}: {
	draft: AiKnowledgeDraft;
	isReceived: boolean;
	chatId?: string;
	messageId?: string;
}) {
	const { toast } = useToast();
	const [updateKnowledge, updateState] = useUpdateAdminChatbotKnowledgeMutation();
	const [updateDraftStatus, updateDraftStatusState] = useUpdateAiKnowledgeDraftStatusMutation();
	const [title, setTitle] = useState(draft.title);
	const [content, setContent] = useState(draft.content);
	const [tags, setTags] = useState(draft.tags.join(", "));
	const [entryType, setEntryType] = useState<"KNOWLEDGE" | "RULE">(
		draft.entryType ?? "KNOWLEDGE",
	);
	const [status, setStatus] = useState<"pending" | "accepted" | "rejected">(
		draft.status ?? "pending",
	);

	const isBusy = updateState.isLoading || updateDraftStatusState.isLoading;
	const parsedTags = () =>
		tags
			.split(",")
			.map((tag) => tag.trim())
			.filter(Boolean);

	async function persistDraftStatus(nextStatus: "accepted" | "rejected") {
		if (!chatId || !messageId) return;
		await updateDraftStatus({ chatId, messageId, status: nextStatus }).unwrap();
	}

	async function saveDraft(
		isActive: boolean,
		values?: {
			title: string;
			content: string;
			tags: string[];
			entryType?: "KNOWLEDGE" | "RULE";
		},
	) {
		const nextTitle = values?.title ?? title;
		const nextContent = values?.content ?? content;
		const nextTags = (values?.tags ?? parsedTags()).filter(
			(tag) => !["rejected", "review-needed"].includes(tag),
		);
		const nextEntryType = values?.entryType ?? entryType;

		if (!nextTitle.trim() || !nextContent.trim()) {
			toast({
				variant: "destructive",
				title: "Missing knowledge details",
				description: "Please add a title and content before accepting the draft.",
			});
			return;
		}

		try {
			await updateKnowledge({
				id: draft.knowledgeId,
				title: nextTitle.trim(),
				content: nextContent.trim(),
				tags: nextTags,
				entryType: nextEntryType,
				isActive,
			}).unwrap();
			await persistDraftStatus("accepted");
			setStatus("accepted");
			toast({
				title: `AI ${nextEntryType === "RULE" ? "Rule" : "Knowledge"} draft accepted`,
			});
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not update knowledge draft",
				description: errorIntoString(error),
			});
		}
	}

	async function rejectDraft() {
		try {
			await persistDraftStatus("rejected");
			try {
				await updateKnowledge({
					id: draft.knowledgeId,
					title: draft.title,
					content: draft.content,
					tags: Array.from(
						new Set([
							...draft.tags.filter((tag) => tag !== "review-needed"),
							"rejected",
						]),
					),
					entryType: draft.entryType ?? "KNOWLEDGE",
					isActive: false,
				}).unwrap();
			} catch (knowledgeError) {
				if (!/not found/i.test(errorIntoString(knowledgeError))) {
					throw knowledgeError;
				}
			}
			setStatus("rejected");
			toast({ title: "AI Knowledge draft rejected" });
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not reject knowledge draft",
				description: errorIntoString(error),
			});
		}
	}

	if (status !== "pending") {
		const label = entryType === "RULE" ? "Rule" : "Knowledge";
		return (
			<div
				className={cn(
					"mt-3 w-full max-w-md rounded-2xl border bg-background p-4 shadow-custom",
					!isReceived && "self-end",
				)}
			>
				<p className="text-sm font-semibold text-foreground">
					AI {label} draft {status}
				</p>
				<p className="mt-1 text-xs text-muted-foreground">
					{status === "accepted"
						? `Reference saved: ${title}`
						: `Reference rejected: ${draft.title}`}
				</p>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"mt-3 w-full max-w-md rounded-2xl border bg-background p-4 shadow-custom",
				!isReceived && "self-end",
			)}
		>
			<div className="mb-3">
				<p className="text-sm font-semibold text-foreground">
					AI {entryType === "RULE" ? "Rule" : "Knowledge"} draft
				</p>
				<p className="text-xs text-muted-foreground">
					Draft type: {entryType === "RULE" ? "Rule" : "Knowledge"}. Directors can edit,
					accept, or reject it.
				</p>
			</div>
			<div className="space-y-3">
				<label className="block text-xs font-medium text-muted-foreground">
					Draft type
					<select
						value={entryType}
						onChange={(event) =>
							setEntryType(event.target.value as "KNOWLEDGE" | "RULE")
						}
						className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
						disabled={status !== "pending"}
					>
						<option value="KNOWLEDGE">
							Knowledge - approved information Laura can use
						</option>
						<option value="RULE">Rule - behavior Laura must follow</option>
					</select>
				</label>
				<label className="block text-xs font-medium text-muted-foreground">
					Title
					<input
						value={title}
						onChange={(event) => setTitle(event.target.value)}
						className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
						disabled={status !== "pending"}
					/>
				</label>
				<label className="block text-xs font-medium text-muted-foreground">
					Content
					<textarea
						value={content}
						onChange={(event) => setContent(event.target.value)}
						className="mt-1 min-h-32 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
						disabled={status !== "pending"}
					/>
				</label>
				<label className="block text-xs font-medium text-muted-foreground">
					Tags
					<input
						value={tags}
						onChange={(event) => setTags(event.target.value)}
						className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
						disabled={status !== "pending"}
						placeholder="tag one, tag two"
					/>
				</label>
			</div>
			<div className="mt-3 flex flex-wrap gap-2">
				<button
					type="button"
					className="rounded-full bg-secondary px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
					onClick={() => saveDraft(true)}
					disabled={isBusy}
				>
					{updateState.isLoading ? "Saving..." : "Save & accept"}
				</button>
				<button
					type="button"
					className="rounded-full border border-secondary/20 px-4 py-2 text-xs font-semibold text-secondary transition-opacity hover:opacity-80 disabled:opacity-60"
					onClick={() => saveDraft(true, draft)}
					disabled={isBusy}
				>
					Accept draft
				</button>
				<button
					type="button"
					className="rounded-full border border-destructive/20 px-4 py-2 text-xs font-semibold text-destructive transition-opacity hover:opacity-80 disabled:opacity-60"
					onClick={rejectDraft}
					disabled={isBusy}
				>
					{updateDraftStatusState.isLoading ? "Rejecting..." : "Reject"}
				</button>
			</div>
		</div>
	);
}

function SupportTicketDraftCard({
	draft,
	chatId,
	messageId,
	isReceived,
}: {
	draft: SupportTicketDraft;
	chatId?: string;
	messageId?: string;
	isReceived: boolean;
}) {
	const { toast } = useToast();
	const [createTicket, createState] = useCreateSupportTicketMutation();
	const [removeDraft, removeDraftState] = useRemoveSupportTicketDraftMutation();
	const [markSubmitted, markSubmittedState] = useMarkSupportTicketDraftSubmittedMutation();
	const [category, setCategory] = useState<SupportTicketCategory>(draft.category);
	const [subject, setSubject] = useState(draft.subject);
	const [description, setDescription] = useState(draft.description);
	const [draftStatus, setDraftStatus] = useState<"pending" | "cancelled" | "submitted">(
		draft.status ?? "pending",
	);
	const [createdTicketId, setCreatedTicketId] = useState<string | null>(draft.ticketId || null);

	async function onCreateTicket() {
		if (!subject.trim() || !description.trim()) {
			toast({
				variant: "destructive",
				title: "Missing ticket details",
				description: "Please add a subject and description before creating the ticket.",
			});
			return;
		}

		try {
			const ticket = await createTicket({
				category,
				subject: subject.trim(),
				description: description.trim(),
			}).unwrap();
			if (chatId && messageId) {
				await markSubmitted({ chatId, messageId, ticketId: ticket._id }).unwrap();
			}
			setCreatedTicketId(ticket._id);
			setDraftStatus("submitted");
			toast({ title: "Support ticket created" });
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not create support ticket",
				description: errorIntoString(error),
			});
		}
	}

	async function onCancelDraft() {
		if (!chatId || !messageId) return;
		try {
			await removeDraft({ chatId, messageId }).unwrap();
			setDraftStatus("cancelled");
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not remove draft",
				description: errorIntoString(error),
			});
		}
	}

	if (draftStatus !== "pending") {
		return (
			<div
				className={cn(
					"mt-3 w-full max-w-sm rounded-2xl border bg-background p-4 shadow-custom",
					!isReceived && "self-end",
				)}
			>
				<p className="text-sm font-semibold text-foreground">
					Support ticket draft {draftStatus === "submitted" ? "submitted" : "cancelled"}
				</p>
				<p className="mt-1 text-xs text-muted-foreground">
					{draftStatus === "submitted"
						? "The support ticket was created from this draft."
						: "The user cancelled this support ticket draft. No ticket was created."}
				</p>
				{draftStatus === "submitted" && createdTicketId ? (
					<Link
						href={`/profile/inbox/ticket/${createdTicketId}`}
						className="mt-3 inline-flex rounded-full bg-secondary px-4 py-2 text-xs font-semibold text-white"
					>
						View support ticket
					</Link>
				) : null}
			</div>
		);
	}

	return (
		<div
			className={cn(
				"mt-3 w-full max-w-sm rounded-2xl border bg-background p-4 shadow-custom",
				!isReceived && "self-end",
			)}
		>
			<div className="mb-3">
				<p className="text-sm font-semibold text-foreground">Support ticket draft</p>
				<p className="text-xs text-muted-foreground">
					You can edit this before creating the ticket.
				</p>
			</div>
			<div className="space-y-3">
				<label className="block text-xs font-medium text-muted-foreground">
					Category
					<select
						value={category}
						onChange={(event) =>
							setCategory(event.target.value as SupportTicketCategory)
						}
						className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
						disabled={draftStatus !== "pending"}
					>
						<option value={SupportTicketCategory.OTHER}>Enquiry</option>
						<option value={SupportTicketCategory.ORDER}>Order</option>
						<option value={SupportTicketCategory.HOSTING}>Hosting</option>
						<option value={SupportTicketCategory.PLAN}>Plan</option>
					</select>
				</label>
				<label className="block text-xs font-medium text-muted-foreground">
					Subject
					<input
						value={subject}
						onChange={(event) => setSubject(event.target.value)}
						className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
						disabled={draftStatus !== "pending"}
					/>
				</label>
				<label className="block text-xs font-medium text-muted-foreground">
					Description
					<textarea
						value={description}
						onChange={(event) => setDescription(event.target.value)}
						className="mt-1 min-h-28 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
						disabled={draftStatus !== "pending"}
					/>
				</label>
			</div>
			<div className="mt-3 flex flex-wrap gap-2">
				<button
					type="button"
					className="rounded-full bg-secondary px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
					onClick={onCreateTicket}
					disabled={createState.isLoading || markSubmittedState.isLoading}
				>
					{createState.isLoading || markSubmittedState.isLoading
						? "Creating..."
						: "Create ticket"}
				</button>
				<button
					type="button"
					className="rounded-full border border-secondary/20 px-4 py-2 text-xs font-semibold text-secondary transition-opacity hover:opacity-80 disabled:opacity-60"
					onClick={onCancelDraft}
					disabled={
						!chatId ||
						!messageId ||
						createState.isLoading ||
						removeDraftState.isLoading ||
						markSubmittedState.isLoading
					}
				>
					{removeDraftState.isLoading ? "Cancelling..." : "Cancel"}
				</button>
			</div>
		</div>
	);
}

function CatalogSuggestionCards({
	suggestions,
	isReceived,
}: {
	suggestions: CatalogSuggestion[];
	isReceived: boolean;
}) {
	const cleanMeta = (meta?: string) =>
		meta
			?.split("•")
			.map((part) => part.trim())
			.filter((part) => !/^from\s+[A-Z]{3}\b/i.test(part))
			.filter((part) => !/^delivery\b/i.test(part))
			.join(" • ");

	return (
		<div className={cn("mb-5 mt-2 grid w-[20rem] max-w-full gap-4", !isReceived && "self-end")}>
			{suggestions.map((suggestion, index) => {
				if (suggestion.type === "service") {
					return (
						<ServiceCatalogSuggestionCard
							key={`${suggestion.type}-${suggestion.service?._id || suggestion.url || suggestion.title}`}
							suggestion={suggestion}
							index={index}
						/>
					);
				}

				const safeUrl =
					isSafeHttpOrHttpsUrl(suggestion.url) || suggestion.url.startsWith("/")
						? suggestion.url
						: null;

				const card = (
					<BlogSuggestionCard suggestion={suggestion} meta={cleanMeta(suggestion.meta)} />
				);

				return safeUrl ? (
					<Link
						key={`${suggestion.type}-${suggestion.url}`}
						href={safeUrl}
						target="_blank"
						rel="noopener noreferrer"
					>
						{card}
					</Link>
				) : (
					<div key={`${suggestion.type}-${suggestion.title}`}>{card}</div>
				);
			})}
		</div>
	);
}

function ServiceCatalogSuggestionCard({
	suggestion,
	index,
}: {
	suggestion: CatalogSuggestion;
	index: number;
}) {
	const slug = useMemo(() => getServiceSlugFromSuggestionUrl(suggestion.url), [suggestion.url]);
	const { data, isFetching } = useGetServiceQuery(slug ?? "", {
		skip: Boolean(suggestion.service) || !slug,
	});
	const service = suggestion.service ?? data;

	if (service) {
		return (
			<div className="w-[20rem] max-w-full">
				<ServiceCard
					service={service}
					mediaPriority={index === 0}
					listing={{
						itemListId: "laura-chat-catalog",
						itemListName: "Laura chat catalog",
						index: index + 1,
					}}
				/>
			</div>
		);
	}

	if (isFetching) {
		return (
			<div className="w-[20rem] max-w-full">
				<ServiceCardSkeleton />
			</div>
		);
	}

	const safeUrl =
		isSafeHttpOrHttpsUrl(suggestion.url) || suggestion.url.startsWith("/")
			? suggestion.url
			: null;
	const card = <ServiceSuggestionCard suggestion={suggestion} />;

	return safeUrl ? (
		<Link href={safeUrl} target="_blank" rel="noopener noreferrer">
			{card}
		</Link>
	) : (
		<div>{card}</div>
	);
}

function ServiceSuggestionCard({ suggestion }: { suggestion: CatalogSuggestion }) {
	const tags = suggestion.tags?.filter(Boolean).slice(0, 5) ?? [];
	const safeImageUrl =
		isSafeHttpOrHttpsUrl(suggestion.imageUrl) || suggestion.imageUrl?.startsWith("/")
			? suggestion.imageUrl
			: "";

	return (
		<div className="relative flex h-[calc(21rem-1rem+0.5rem)] cursor-pointer flex-col overflow-hidden rounded-lg bg-card text-left shadow-custom transition-transform hover:-translate-y-1">
			<div className="relative block h-44 w-full shrink-0 overflow-hidden rounded-t-lg bg-muted/40">
				{safeImageUrl ? (
					<img
						src={safeImageUrl}
						alt=""
						className="size-full object-cover object-center"
					/>
				) : null}
			</div>

			<div className="flex min-h-0 flex-1 flex-col px-3 py-2 outline-none">
				<div className="flex shrink-0 flex-col gap-0.5">
					<span className="flex h-[1.125rem] shrink-0 items-center text-[11px] leading-tight text-brand-primary line-clamp-1">
						{suggestion.label || "Uncategorized"}
					</span>
					<div className="text-sm leading-snug">
						<div className="flex h-[calc(3lh+0.25rem+2rem)] shrink-0 flex-col gap-1">
							<ServiceCardTitleClamp text={formatServiceDisplayTitle(suggestion.title)} />
							<ServiceSuggestionPills tags={tags} />
						</div>
					</div>
				</div>

				<div className="mt-auto flex min-h-[1.125rem] shrink-0 items-center justify-end pb-4 leading-tight" />
			</div>
		</div>
	);
}

function ServiceSuggestionPills({ tags }: { tags: string[] }) {
	if (tags.length === 0) {
		return <div className="h-7 min-w-0 shrink-0" aria-hidden />;
	}

	return (
		<div className="relative h-7 min-w-0 shrink-0">
			<div className="flex h-full min-h-0 min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
				{tags.map((tag) => (
					<span
						key={tag}
						className="inline-flex max-w-[11rem] shrink-0 snap-start items-center truncate rounded-full bg-secondary/5 px-2 py-1 text-[9px] font-normal leading-tight text-secondary"
						title={tag}
					>
						{tag}
					</span>
				))}
			</div>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-9 bg-gradient-to-l from-card via-card/85 to-transparent dark:from-card dark:via-card/85"
			/>
		</div>
	);
}

function BlogSuggestionCard({
	suggestion,
	meta,
}: {
	suggestion: CatalogSuggestion;
	meta?: string;
}) {
	const safeImageUrl =
		isSafeHttpOrHttpsUrl(suggestion.imageUrl) || suggestion.imageUrl?.startsWith("/")
			? suggestion.imageUrl
			: "";

	return (
		<div className="relative flex h-[22rem] flex-col rounded-lg border bg-card text-card-foreground !shadow-custom">
			<div className="relative h-52 w-full overflow-hidden rounded-t-lg bg-muted/40 p-0">
				{safeImageUrl ? (
					<img
						src={safeImageUrl}
						alt=""
						className="size-full rounded-t-lg object-cover"
					/>
				) : null}
			</div>

			<div className="inline-flex h-10 items-center justify-between px-4 text-xsm">
				<span className="text-[11px] text-muted-foreground">
					{suggestion.label || "Article"}
				</span>
				<div className="flex h-6 w-20 items-center justify-center rounded-full bg-tertiary font-medium uppercase text-white">
					Free
				</div>
			</div>

			<div className="inline-flex flex-1 px-4 pt-1 text-sm font-medium leading-snug">
				<span className="line-clamp-3">{formatServiceDisplayTitle(suggestion.title)}</span>
			</div>

			<div className="inline-flex h-6 items-center justify-end px-4 text-[11px] text-grey-foreground">
				<span>{meta || "Article"}</span>
			</div>
		</div>
	);
}

function FileLayer({ url }: { url: string }) {
	const safeUrl = isSafeHttpOrHttpsUrl(url) ? url : null;
	const filename = removeUUIDv4Prefix(extractFromS3Url(url));
	const filetype = filename.split(".")[-1];

	if (safeUrl == null) {
		return (
			<div className="mb-2 flex gap-2 rounded-lg bg-background p-2 shadow-sm">
				<Image
					src={getImageType(filetype)}
					draggable="false"
					aria-hidden
					alt=""
					width={20}
					height={20}
					className="size-5"
				/>
				<span className="text-ellipsis text-xs text-foreground md:text-sm">{filename}</span>
			</div>
		);
	}
	return (
		<Link
			href={safeUrl}
			target="_blank"
			rel="noopener noreferrer"
			className="mb-2 flex gap-2 rounded-lg bg-background p-2 shadow-sm transition-shadow hover:shadow-md"
		>
			<Image
				src={getImageType(filetype)}
				draggable="false"
				aria-hidden
				alt=""
				width={20}
				height={20}
				className="size-5"
			/>

			<span className="text-ellipsis text-xs text-foreground md:text-sm">{filename}</span>
		</Link>
	);
}

function MessageContent({ text }: { text: string }) {
	const normalizedText = text
		.replace(/\s+\*\s*([A-Z][^:\n]{1,60}:)/g, "\n\n**$1**")
		.replace(/^\*\s*([A-Z][^:\n]{1,60}:)/gm, "**$1**");

	return (
		<>
			{normalizedText.split(linkRegex).map((part, index) => {
				const trimmed = part.trim();
				const looksLikeHttpUrl =
					trimmed.toLowerCase().startsWith("http://") ||
					trimmed.toLowerCase().startsWith("https://");

				if (looksLikeHttpUrl && isSafeHttpOrHttpsUrl(part)) {
					return (
						<a
							key={`${part}-${index}`}
							target="_blank"
							rel="noopener noreferrer"
							href={part}
							className="break-all underline decoration-solid"
						>
							{part}
						</a>
					);
				}

				return (
					<span key={`${part}-${index}`}>
						{part.split(boldRegex).map((segment, segmentIndex) => {
							if (segment.startsWith("**") && segment.endsWith("**")) {
								return (
									<strong
										key={`${segment}-${segmentIndex}`}
										className="font-semibold"
									>
										{segment.slice(2, -2)}
									</strong>
								);
							}
							if (segment.startsWith("*") && segment.endsWith("*")) {
								return (
									<strong
										key={`${segment}-${segmentIndex}`}
										className="font-semibold"
									>
										{segment.slice(1, -1)}
									</strong>
								);
							}
							return segment;
						})}
					</span>
				);
			})}
		</>
	);
}

function MessageStatus({ isRead }: { isRead?: boolean }) {
	if (isRead === undefined) return null;

	return (
		<span className="inline-flex items-center">
			{isRead ? (
				<CheckCheckIcon className="size-3 text-tertiary" aria-label="Read" />
			) : (
				<CheckIcon className="size-3 text-muted-foreground" aria-label="Sent" />
			)}
		</span>
	);
}

export default memo(Message);
